import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators
} from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  MixtureCalculationDto,
  ReportsControllerService,
  WeldingRateDto
} from '@mobiletract/kobize-service-angular-client';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectButtonModule } from 'primeng/selectbutton';
import { TextareaModule } from 'primeng/textarea';
import { firstValueFrom, startWith } from 'rxjs';
import {
  formatPercentageInput,
  normalizePercentageForApi,
  percentageInputValidator
} from './percentage-input.util';

/** Values aligned with legacy Ekowitryna `MixtureType`. */
export type MixtureDialogKind = 'MIXTURE' | 'WELDING';

const MIXTURE_CARD_WELDING_FIELD_KEYS = [
  'iron',
  'nickel',
  'manganese',
  'chromium',
  'dust',
  'carbon',
  'nitrogen'
] as const;

type WeldingFieldKey = (typeof MIXTURE_CARD_WELDING_FIELD_KEYS)[number];

interface MixtureTypeOption {
  label: string;
  value: MixtureDialogKind;
}

interface WeldingFieldDefinition {
  key: WeldingFieldKey;
  casNumber: string;
}

type WeldingFormValues = Record<WeldingFieldKey, string | null>;
const MIXTURE_NAME_MIN_LENGTH = 3;
const MIXTURE_NAME_MAX_LENGTH = 100;

function noWhitespaceValidator(control: AbstractControl): ValidationErrors | null {
  const value = control.value;
  if (typeof value !== 'string') {
    return null;
  }
  return value.trim().length > 0 ? null : { whitespace: true };
}

const WELDING_FIELD_DEFINITIONS: WeldingFieldDefinition[] = [
  { key: 'iron', casNumber: '7439-89-6' },
  { key: 'nickel', casNumber: '7440-02-0' },
  { key: 'manganese', casNumber: '7439-96-5' },
  { key: 'chromium', casNumber: '7440-47-3' },
  { key: 'dust', casNumber: 'Pył całkowity, pyły pozostałe' },
  { key: 'carbon', casNumber: '630-08-0' },
  { key: 'nitrogen', casNumber: '10102-43-9' }
];

@Component({
  selector: 'app-report-mixture-card-dialog',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    ReactiveFormsModule,
    DialogModule,
    ButtonModule,
    InputTextModule,
    TextareaModule,
    InputNumberModule,
    SelectButtonModule
  ],
  templateUrl: './report-mixture-card-dialog.component.html',
  styleUrl: './report-mixture-card-dialog.component.css'
})
export class ReportMixtureCardDialogComponent implements OnInit, OnChanges {
  private readonly reportsApi = inject(ReportsControllerService);
  private readonly translate = inject(TranslateService);
  private readonly messageService = inject(MessageService);
  private readonly destroyRef = inject(DestroyRef);

  @Input({ required: true }) visible = false;
  @Output() visibleChange = new EventEmitter<boolean>();

  @Input({ required: true }) reportId: string | null = null;
  @Input({ required: true }) companyId: string | null = null;
  /** When null, dialog is in create mode. */
  @Input() mixture: MixtureCalculationDto | null = null;

  @Output() saved = new EventEmitter<void>();

  readonly form = new FormGroup({
    name: new FormControl<string>('', {
      nonNullable: true,
      validators: [
        Validators.required,
        noWhitespaceValidator,
        Validators.minLength(MIXTURE_NAME_MIN_LENGTH),
        Validators.maxLength(MIXTURE_NAME_MAX_LENGTH)
      ]
    }),
    mass: new FormControl<number | null>(null, {
      validators: [Validators.required, Validators.min(0)]
    }),
    description: new FormControl<string>('', { nonNullable: true }),
    mixtureType: new FormControl<MixtureDialogKind>('MIXTURE', {
      nonNullable: true,
      validators: [Validators.required]
    }),
    iron: new FormControl<string | null>(null),
    nickel: new FormControl<string | null>(null),
    manganese: new FormControl<string | null>(null),
    chromium: new FormControl<string | null>(null),
    dust: new FormControl<string | null>(null),
    carbon: new FormControl<string | null>(null),
    nitrogen: new FormControl<string | null>(null)
  });

  mixtureTypeOptions: MixtureTypeOption[] = [];
  readonly weldingFieldKeys = MIXTURE_CARD_WELDING_FIELD_KEYS;
  saving = false;
  private weldingRatesCache: WeldingRateDto[] | null = null;
  private weldingRatesCacheCompanyId: string | null = null;

  ngOnInit(): void {
    this.rebuildMixtureTypeOptions();
    this.translate.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.rebuildMixtureTypeOptions();
    });

    this.form
      .get('mixtureType')!
      .valueChanges.pipe(startWith(this.form.get('mixtureType')!.value), takeUntilDestroyed(this.destroyRef))
      .subscribe((t) => {
        this.applyWeldingValidators(t);
        if (t === 'WELDING') {
          void this.ensureWeldingRatesLoaded();
        }
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['companyId'] && !changes['companyId'].firstChange) {
      const previous = changes['companyId'].previousValue as string | null;
      const current = changes['companyId'].currentValue as string | null;
      if (previous !== current) {
        this.weldingRatesCache = null;
        this.weldingRatesCacheCompanyId = null;
      }
    }
    if (changes['visible']?.currentValue === true) {
      this.patchFormFromInputs();
    }
    if (changes['mixture'] && this.visible) {
      this.patchFormFromInputs();
    }
  }

  private rebuildMixtureTypeOptions(): void {
    this.mixtureTypeOptions = [
      {
        label: this.translate.instant('reports.fill.mixtureDialog.typeMixture'),
        value: 'MIXTURE'
      },
      {
        label: this.translate.instant('reports.fill.mixtureDialog.typeWelding'),
        value: 'WELDING'
      }
    ];
  }

  private applyWeldingValidators(mixtureType: MixtureDialogKind | null): void {
    const weldingRequired = mixtureType === 'WELDING';
    for (const key of MIXTURE_CARD_WELDING_FIELD_KEYS) {
      const c = this.form.get(key)!;
      if (weldingRequired) {
        c.setValidators([Validators.required, percentageInputValidator()]);
      } else {
        c.clearValidators();
        c.setValue(null, { emitEvent: false });
      }
      c.updateValueAndValidity({ emitEvent: false });
    }
  }

  private patchFormFromInputs(): void {
    const m = this.mixture;
    if (m) {
      const editingType = m.type as MixtureDialogKind;
      const valuesFromMixture = this.mapWeldingValuesFromMixture(m);
      this.form.patchValue({
        name: m.name,
        mass: m.mass,
        description: m.description ?? '',
        mixtureType: editingType,
        ...valuesFromMixture
      });
    } else {
      this.form.reset({
        name: '',
        mass: null,
        description: '',
        mixtureType: 'MIXTURE',
        iron: null,
        nickel: null,
        manganese: null,
        chromium: null,
        dust: null,
        carbon: null,
        nitrogen: null
      });
    }
    this.applyWeldingValidators(this.form.get('mixtureType')!.value);
  }

  /**
   * Czyta wartości po tych samych numerach CAS, którymi zapisuje onSave.
   *
   * Wcześniej lista numerów była tu przepisana z ręki i rozjechała się z
   * WELDING_FIELD_DEFINITIONS: pył zapisywał się pod nazwą z KOBiZE, a odczytywał pod
   * nieaktualnym '0000000-00-0', więc jego pole zostawało puste przy edycji mieszaniny.
   */
  private mapWeldingValuesFromMixture(mixture: MixtureCalculationDto): WeldingFormValues {
    const byCas = new Map<string, string | null>();
    for (const row of mixture.recordsCalculations ?? []) {
      byCas.set(row.casNumber, formatPercentageInput(row.usagePercentage));
    }

    const values = {} as WeldingFormValues;
    for (const def of WELDING_FIELD_DEFINITIONS) {
      values[def.key] = byCas.get(def.casNumber) ?? null;
    }
    return values;
  }

  get dialogHeaderKey(): string {
    return this.mixture
      ? 'reports.fill.mixtureDialog.titleEdit'
      : 'reports.fill.mixtureDialog.titleCreate';
  }

  get isWeldingSectionVisible(): boolean {
    return this.form.get('mixtureType')!.value === 'WELDING';
  }

  get isEditMode(): boolean {
    return this.mixture != null;
  }

  onVisibleChange(open: boolean): void {
    this.visibleChange.emit(open);
    if (!open) {
      this.saving = false;
    }
  }

  weldingFieldLabel(key: WeldingFieldKey): string {
    return `reports.fill.mixtureDialog.welding.${key}`;
  }

  private async ensureWeldingRatesLoaded(): Promise<void> {
    if (this.isEditMode) {
      return;
    }
    if (!this.companyId) {
      return;
    }
    if (this.weldingRatesCache && this.weldingRatesCacheCompanyId === this.companyId) {
      this.applyWeldingRates(this.weldingRatesCache);
      return;
    }
    try {
      const response = await firstValueFrom(this.reportsApi.getWeldingRates(this.companyId));
      this.weldingRatesCache = response.records ?? [];
      this.weldingRatesCacheCompanyId = this.companyId;
      this.applyWeldingRates(this.weldingRatesCache);
    } catch (err: unknown) {
      const detail = this.mapHttpErrorDetail(err);
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('reports.toast.errorTitle'),
        detail
      });
    }
  }

  private applyWeldingRates(records: WeldingRateDto[]): void {
    const byKey = new Map<string, string | null>();
    const byCas = new Map<string, string | null>();
    for (const rec of records) {
      const normalized = formatPercentageInput(rec.usagePercentage);
      byKey.set(rec.key, normalized);
      byCas.set(rec.casNumber, normalized);
    }
    for (const def of WELDING_FIELD_DEFINITIONS) {
      const value = byKey.get(def.key) ?? byCas.get(def.casNumber) ?? null;
      this.form.get(def.key)!.setValue(value, { emitEvent: false });
    }
  }

  async onSave(): Promise<void> {
    this.form.markAllAsTouched();
    if (!this.form.valid || !this.reportId || !this.companyId) {
      return;
    }

    const raw = this.form.getRawValue();
    this.saving = true;
    try {
      const baseBody = {
        name: raw.name.trim(),
        mass: raw.mass as number,
        description: raw.description.trim() || undefined
      };
      if (raw.mixtureType === 'WELDING') {
        const records = WELDING_FIELD_DEFINITIONS.map((def) => {
          const normalized = normalizePercentageForApi(raw[def.key]);
          if (!normalized) {
            throw new Error(`Invalid percentage for ${def.key}`);
          }
          return {
            casNumber: def.casNumber,
            usagePercentage: normalized as unknown as number
          };
        });
        if (this.mixture) {
          await firstValueFrom(
            this.reportsApi.updateWeldingMixture(this.reportId, this.mixture.id, this.companyId, {
              ...baseBody,
              records
            })
          );
        } else {
          await firstValueFrom(
            this.reportsApi.addWeldingMixture(this.reportId, this.companyId, {
              ...baseBody,
              records
            })
          );
        }
      } else {
        if (this.mixture) {
          await firstValueFrom(
            this.reportsApi.updateMixture(this.reportId, this.mixture.id, this.companyId, baseBody)
          );
        } else {
          await firstValueFrom(this.reportsApi.addMixture(this.reportId, this.companyId, baseBody));
        }
      }
      this.saved.emit();
      this.visibleChange.emit(false);
    } catch (err: unknown) {
      const detail = this.mapHttpErrorDetail(err);
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('reports.toast.errorTitle'),
        detail
      });
    } finally {
      this.saving = false;
    }
  }

  private mapHttpErrorDetail(err: unknown): string {
    const fallback = this.translate.instant('common.somethingWentWrong');
    if (!(err instanceof HttpErrorResponse)) {
      return fallback;
    }
    const body = err.error;
    if (body && typeof body === 'object') {
      const msg = (body as Record<string, unknown>)['message'];
      if (typeof msg === 'string' && msg.trim().length > 0) {
        return msg.trim();
      }
      return this.statusFallback(err.status, fallback);
    }
    if (typeof body === 'string') {
      const text = body.trim();
      if (text.length === 0) {
        return this.statusFallback(err.status, fallback);
      }
      try {
        const parsed = JSON.parse(text) as Record<string, unknown>;
        const msg = parsed['message'];
        if (typeof msg === 'string' && msg.trim().length > 0) {
          return msg.trim();
        }
      } catch {
        return this.statusFallback(err.status, fallback);
      }
    }
    return this.statusFallback(err.status, fallback);
  }

  private statusFallback(status: number, fallback: string): string {
    switch (status) {
      case 400:
        return this.translate.instant('reports.errors.badRequest', { status });
      case 401:
        return this.translate.instant('reports.errors.unauthorized');
      case 403:
        return this.translate.instant('reports.errors.forbidden');
      case 404:
        return this.translate.instant('reports.errors.notFound');
      case 409:
        return this.translate.instant('reports.errors.conflict');
      default:
        return status > 0
          ? this.translate.instant('reports.errors.httpStatus', { status })
          : fallback;
    }
  }

  fieldError(controlName: string): string | null {
    const c = this.form.get(controlName);
    if (!c || !c.touched || !c.errors) {
      return null;
    }
    if (c.errors['required']) {
      return this.translate.instant('reports.form.fieldRequired');
    }
    if (c.errors['whitespace']) {
      return this.translate.instant('reports.form.fieldRequired');
    }
    if (c.errors['minlength']) {
      return this.translate.instant('reports.fill.mixtureDialog.errorNameMinLength', {
        min: MIXTURE_NAME_MIN_LENGTH
      });
    }
    if (c.errors['maxlength']) {
      return this.translate.instant('reports.fill.mixtureDialog.errorNameMaxLength', {
        max: MIXTURE_NAME_MAX_LENGTH
      });
    }
    if (c.errors['min']) {
      if (this.weldingFieldKeys.includes(controlName as WeldingFieldKey)) {
        return this.translate.instant('reports.fill.mixtureDialog.errorMinPercent');
      }
      return this.translate.instant('reports.fill.mixtureDialog.errorMin');
    }
    if (c.errors['max']) {
      return this.translate.instant('reports.fill.mixtureDialog.errorMaxPercent');
    }
    if (c.errors['invalidDecimal'] || c.errors['maxFractionDigits']) {
      return this.translate.instant('reports.fill.mixtureDialog.errorInvalidPercentFormat');
    }
    return null;
  }
}
